import { useState, useEffect } from "react";
import {
  Box, Stack, Typography, Button, IconButton, TextField, Switch,
  Alert, Chip, CircularProgress, Card, Divider,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ManagerService } from "../../client";
import {
  MONTHS, emptyPeriod, emptyClosure, fromServer, toPayload, periodBadge,
  complementDates, copyFirstOpenDay, errorsFor, generalErrors, hasNoDates,
} from "./scheduleLogic";

// Sezione "Orari e chiusure" del pannello manager. Il negoziante indica gli
// orari di apertura per periodo e i giorni di chiusura; le date sono giorno +
// mese e si ripetono ogni anno. Parla con /api/manager/schedule/<token>/.

const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1));

const fieldSx = { backgroundColor: "#fff" };

// Larghezza della colonna "interruttore + giorno + Aperto/Chiuso" su schermo largo.
const DAY_COLUMN = 232;

// Giorno + mese: due menu (quelli nativi: sul telefono si apre il selettore del
// sistema), niente anno e niente date scritte a mano.
function DateSelect({ label, day, month, onChange, error }) {
  return (
    <Box>
      <Typography fontSize={12} fontWeight={700} color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" gap={1}>
        <TextField
          select size="small" value={day} sx={{ ...fieldSx, width: 88 }} error={Boolean(error)}
          onChange={(e) => onChange({ day: e.target.value, month })}
          slotProps={{ select: { native: true }, htmlInput: { "aria-label": `${label}: giorno` } }}
        >
          <option value="">gg</option>
          {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
        </TextField>
        <TextField
          select size="small" value={month} sx={{ ...fieldSx, width: 146 }} error={Boolean(error)}
          onChange={(e) => onChange({ day, month: e.target.value })}
          slotProps={{ select: { native: true }, htmlInput: { "aria-label": `${label}: mese` } }}
        >
          <option value="">mese</option>
          {MONTHS.map((name, i) => <option key={name} value={String(i + 1)}>{name}</option>)}
        </TextField>
      </Stack>
    </Box>
  );
}

function TimeField({ label, value, onChange, disabled, error }) {
  return (
    <TextField
      type="time" size="small" label={label} value={value} disabled={disabled}
      onChange={(e) => onChange(e.target.value)} error={error}
      slotProps={{ inputLabel: { shrink: true } }}
      sx={{ ...fieldSx, width: "100%" }}
    />
  );
}

function DayRow({ day, onChange, error }) {
  const set = (patch) => onChange({ ...day, ...patch });
  return (
    <Box sx={{ py: 1.25, borderTop: "1px solid #EEEEE9" }}>
      <Box
        sx={{
          display: "grid", alignItems: "center", gap: 1.25,
          gridTemplateColumns: { xs: "1fr 1fr", md: `${DAY_COLUMN}px repeat(4, minmax(0, 1fr))` },
        }}
      >
        <Stack
          direction="row" alignItems="center" gap={0.5}
          sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}
        >
          <Switch
            color="primary" checked={day.is_open}
            onChange={(e) => set({ is_open: e.target.checked })}
            inputProps={{ "aria-label": `${day.name}: aperto` }}
          />
          <Typography fontWeight={700} fontSize={14} sx={{ minWidth: 82 }}>{day.name}</Typography>
          <Typography fontSize={12} color={day.is_open ? "primary.main" : "text.secondary"} fontWeight={600}>
            {day.is_open ? "Aperto" : "Chiuso"}
          </Typography>
        </Stack>
        <TimeField label="Apre" value={day.opens} disabled={!day.is_open} error={Boolean(error)}
          onChange={(opens) => set({ opens })} />
        <TimeField label="Chiude" value={day.closes} disabled={!day.is_open} error={Boolean(error)}
          onChange={(closes) => set({ closes })} />
        <TimeField label="Inizio pausa" value={day.break_from} disabled={!day.is_open}
          onChange={(break_from) => set({ break_from })} />
        <TimeField label="Fine pausa" value={day.break_to} disabled={!day.is_open}
          onChange={(break_to) => set({ break_to })} />
      </Box>
      {error && (
        <Typography color="error" fontSize={12} fontWeight={700} sx={{ mt: 0.5, ml: { md: `${DAY_COLUMN + 10}px` } }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

function PeriodCard({ period, number, errors, onChange, onRemove, id }) {
  const [copyNote, setCopyNote] = useState(null);
  const set = (patch) => onChange({ ...period, ...patch });
  const setDay = (weekday, day) => set({ days: period.days.map((d, i) => (i === weekday ? day : d)) });
  const saved = Boolean(period.id);

  const copy = () => {
    const result = copyFirstOpenDay(period.days);
    if (!result.source) {
      setCopyNote({ ok: false, text: "Compila prima apertura e chiusura di un giorno aperto." });
      return;
    }
    if (result.copied === 0) {
      setCopyNote({ ok: false, text: "Non ci sono altri giorni aperti su cui copiare." });
      return;
    }
    set({ days: result.days });
    setCopyNote({
      ok: true,
      text: `Orari di ${result.source.name} copiati su ${result.copied} ${result.copied === 1 ? "giorno" : "giorni"}.`,
    });
  };

  const badge = periodBadge(period);
  return (
    <Card id={id} sx={{ p: { xs: 1.5, md: 2.5 }, mb: 2, borderRadius: "14px" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography fontWeight={700} fontSize={16}>{`Periodo ${number}`}</Typography>
          <Chip
            size="small" label={badge}
            color={hasNoDates(period) ? "default" : "primary"}
            variant={hasNoDates(period) ? "outlined" : "filled"}
            sx={{ fontWeight: 600 }}
          />
        </Stack>
        <Button
          size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onRemove}
          sx={{ fontWeight: 600 }}
        >
          {saved ? "Elimina periodo" : "Togli"}
        </Button>
      </Stack>

      <Box sx={{
        display: "grid", gap: 2, mt: 2, alignItems: "start",
        gridTemplateColumns: { xs: "1fr", md: "minmax(180px, 1.2fr) auto auto" },
      }}>
        <Box>
          <Typography fontSize={12} fontWeight={700} color="text.secondary" sx={{ mb: 0.5 }}>
            Nome del periodo (facoltativo)
          </Typography>
          <TextField
            size="small" fullWidth value={period.name} placeholder="Es. Orario estivo" sx={fieldSx}
            inputProps={{ maxLength: 80, "aria-label": "Nome del periodo" }}
            onChange={(e) => set({ name: e.target.value })}
            error={Boolean(errors.name)} helperText={errors.name}
          />
        </Box>
        <DateSelect
          label="Valido dal" day={period.start_day} month={period.start_month}
          error={errors.dates}
          onChange={({ day, month }) => set({ start_day: day, start_month: month })}
        />
        <DateSelect
          label="Fino al" day={period.end_day} month={period.end_month}
          error={errors.dates}
          onChange={({ day, month }) => set({ end_day: day, end_month: month })}
        />
      </Box>
      <Typography fontSize={12} color={errors.dates ? "error" : "text.secondary"} fontWeight={errors.dates ? 700 : 400} sx={{ mt: 1 }}>
        {errors.dates || "Lascia le date vuote se l'orario vale tutto l'anno. Le date si ripetono ogni anno."}
      </Typography>

      {errors.general && (
        <Typography color="error" fontSize={12} fontWeight={700} sx={{ mt: 1 }}>{errors.general}</Typography>
      )}

      <Box sx={{ mt: 2 }}>
        {period.days.map((day, weekday) => (
          <DayRow key={day.weekday} day={day} error={errors[`day:${weekday}`]}
            onChange={(next) => setDay(weekday, next)} />
        ))}
      </Box>

      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
        <Button
          variant="outlined" size="small" startIcon={<ContentCopyOutlinedIcon />} onClick={copy}
          title="Copia apertura, chiusura e pausa del primo giorno aperto già compilato su tutti gli altri giorni aperti"
          sx={{ borderRadius: "999px", px: 2, borderWidth: 1.5, "&:hover": { borderWidth: 1.5 } }}
        >
          Copia gli orari sugli altri giorni aperti
        </Button>
        {copyNote && (
          <Typography role="status" fontSize={12.5} fontWeight={600} color={copyNote.ok ? "primary.main" : "text.secondary"}>
            {copyNote.text}
          </Typography>
        )}
      </Stack>
    </Card>
  );
}

function ClosureRow({ closure, errors, onChange, onRemove, id }) {
  const set = (patch) => onChange({ ...closure, ...patch });
  return (
    <Box id={id} sx={{ py: 1.75, borderTop: "1px solid #EEEEE9", "&:first-of-type": { borderTop: 0, pt: 0.5 } }}>
      {/* Prima riga: da quando a quando. Seconda: la nota e il cestino. */}
      <Stack direction="row" gap={2.5} flexWrap="wrap">
        <DateSelect
          label="Dal" day={closure.start_day} month={closure.start_month}
          error={errors.start} onChange={({ day, month }) => set({ start_day: day, start_month: month })}
        />
        <DateSelect
          label="Al (vuoto = un giorno solo)" day={closure.end_day} month={closure.end_month}
          error={errors.end} onChange={({ day, month }) => set({ end_day: day, end_month: month })}
        />
      </Stack>
      <Stack direction="row" gap={1} alignItems="flex-end" sx={{ mt: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize={12} fontWeight={700} color="text.secondary" sx={{ mb: 0.5 }}>Nota</Typography>
          <TextField
            size="small" fullWidth value={closure.note} placeholder="Es. Ferie estive" sx={fieldSx}
            inputProps={{ maxLength: 200, "aria-label": "Nota della chiusura" }}
            onChange={(e) => set({ note: e.target.value })}
            error={Boolean(errors.note)} helperText={errors.note}
          />
        </Box>
        <IconButton aria-label="Elimina chiusura" color="error" onClick={onRemove} sx={{ mb: errors.note ? 3 : 0 }}>
          <DeleteOutlineIcon />
        </IconButton>
      </Stack>
      {(errors.start || errors.end || errors.general) && (
        <Typography color="error" fontSize={12} fontWeight={700} sx={{ mt: 0.75 }}>
          {errors.start || errors.end || errors.general}
        </Typography>
      )}
    </Box>
  );
}

export default function SchedulePanel({ token }) {
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState({ name: "", staff: false });
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState([]);
  const [justAdded, setJustAdded] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoadError("Link non valido: riapri il pannello dal link che trovi nell'admin.");
      return;
    }
    ManagerService.schedule(token).then((data) => {
      if (data && data.customer_status && ["closed", "free-expired"].includes(data.customer_status)) {
        window.location.replace("/admin/");
        return;
      }
      if (!data || !Array.isArray(data.periods)) {
        setLoadError("Link non valido o scaduto: riapri il pannello dal link che trovi nell'admin.");
        return;
      }
      setMeta({ name: data.customer ? data.customer.name : "", staff: Boolean(data.staff) });
      setDraft(fromServer(data));
    }).catch(() => {
      setLoadError("Non riesco a caricare gli orari. Controlla la connessione e riprova.");
    });
  }, [token]);

  // Avvisa se si chiude la pagina con modifiche non salvate.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (!justAdded) return;
    const node = document.getElementById(justAdded);
    if (node && node.scrollIntoView) node.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [justAdded]);

  if (loadError) {
    return <Box sx={{ p: 2 }}><Alert severity="error">{loadError}</Alert></Box>;
  }
  if (!draft) {
    return (
      <Stack alignItems="center" justifyContent="center" gap={2} sx={{ minHeight: "60vh", px: 2 }}>
        <CircularProgress color="primary" />
        <Typography fontSize={14} color="text.secondary">Caricamento orari…</Typography>
      </Stack>
    );
  }

  const change = (next) => { setDraft(next); setDirty(true); setMessage(null); };

  const updatePeriod = (index, period) =>
    change({ ...draft, periods: draft.periods.map((p, i) => (i === index ? period : p)) });
  const updateClosure = (index, closure) =>
    change({ ...draft, closures: draft.closures.map((c, i) => (i === index ? closure : c)) });

  // Una riga gia' salvata resta nell'elenco con `remove` (e' cosi' che il server
  // la cancella); una nuova sparisce e basta.
  const removeItem = (list, index) => list.flatMap((item, i) => {
    if (i !== index) return [item];
    return item.id ? [{ ...item, remove: true }] : [];
  });

  const addPeriod = () => {
    const fresh = emptyPeriod();
    const visible = draft.periods.filter((p) => !p.remove);
    // Il SECONDO periodo parte con le date che il primo lascia fuori
    // (primo dal 01/06 al 01/11 -> secondo dal 02/11 al 31/05).
    if (visible.length === 1) Object.assign(fresh, complementDates(visible[0]) || {});
    change({ ...draft, periods: [...draft.periods, fresh] });
    setJustAdded(`periodo-${fresh.key}`);
  };

  const addClosure = () => {
    const fresh = emptyClosure();
    change({ ...draft, closures: [...draft.closures, fresh] });
    setJustAdded(`chiusura-${fresh.key}`);
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    setMessage(null);
    try {
      const data = await ManagerService.saveSchedule(token, toPayload(draft));
      if (data && Array.isArray(data.errors)) {
        setErrors(data.errors);
        setMessage({ severity: "error", text: "Controlla i campi evidenziati: non è stato salvato nulla." });
      } else if (data && Array.isArray(data.periods)) {
        setDraft(fromServer(data));
        setDirty(false);
        setMessage({
          severity: "success",
          text: data.changed ? "Orari e chiusure salvati." : "Nessuna modifica da salvare.",
        });
      } else {
        setMessage({ severity: "error", text: (data && data.error) || "Salvataggio non riuscito. Riprova." });
      }
    } catch (e) {
      setMessage({ severity: "error", text: "Salvataggio non riuscito. Controlla la connessione e riprova." });
    } finally {
      setSaving(false);
    }
  };

  const visiblePeriods = draft.periods.map((period, index) => ({ period, index })).filter((x) => !x.period.remove);
  const visibleClosures = draft.closures.map((closure, index) => ({ closure, index })).filter((x) => !x.closure.remove);
  const general = generalErrors(errors);

  return (
    <Box sx={{ px: { xs: 1.5, md: 3 }, pt: { xs: 1.5, md: 3 }, pb: 0 }}>
      <Typography component="h1" fontSize={22} fontWeight={700}>Orari e chiusure</Typography>
      <Typography fontSize={14} color="text.secondary" sx={{ mt: 0.75, mb: 2, maxWidth: 760, lineHeight: 1.6 }}>
        Indica gli <strong>orari di apertura</strong> e i <strong>giorni di chiusura</strong> di{" "}
        <strong>{meta.name}</strong>. Le date si ripetono ogni anno: non serve indicare l'anno.
        Ogni volta che salvi, il team Outdoora riceve un avviso con quello che hai cambiato.
        Non è obbligatorio compilare niente: puoi tornare quando vuoi.
      </Typography>

      {meta.staff && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Stai modificando come team Outdoora: il salvataggio non manda nessun avviso.
        </Alert>
      )}

      <Typography component="h2" fontSize={17} fontWeight={700}>Orari di apertura</Typography>
      <Typography fontSize={13} color="text.secondary" sx={{ mt: 0.5, mb: 1.5, maxWidth: 760, lineHeight: 1.6 }}>
        Un periodo <strong>senza date</strong> vale tutto l'anno. Se ne aggiungi uno <strong>con date</strong> (per esempio
        l'orario estivo dal 01/06 al 01/11), per quei giorni vale al posto di quello di base. Anche l'inverno si scrive
        con giorno e mese: dal 02/11 al 31/05.
      </Typography>

      {general.map((text) => <Alert key={text} severity="error" sx={{ mb: 2 }}>{text}</Alert>)}

      {visiblePeriods.map(({ period, index }, position) => (
        <PeriodCard
          key={period.key} id={`periodo-${period.key}`} number={position + 1} period={period}
          errors={errorsFor(errors, "period", index)}
          onChange={(next) => updatePeriod(index, next)}
          onRemove={() => change({ ...draft, periods: removeItem(draft.periods, index) })}
        />
      ))}
      <Button
        variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addPeriod}
        sx={{ borderStyle: "dashed", borderWidth: 1.5, "&:hover": { borderStyle: "dashed", borderWidth: 1.5 }, mb: 3 }}
      >
        {visiblePeriods.length === 0 ? "Aggiungi un periodo" : "Aggiungi un periodo con date"}
      </Button>

      <Typography component="h2" fontSize={17} fontWeight={700}>Giorni di chiusura</Typography>
      <Typography fontSize={13} color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        Ferie, festivi, manutenzione. Si ripetono ogni anno; lascia "Al" vuoto per un giorno solo.
      </Typography>
      <Card sx={{ p: { xs: 1.5, md: 2.5 }, mb: 2, borderRadius: "14px" }}>
        {visibleClosures.length === 0 && (
          <Typography fontSize={14} color="text.secondary">Nessuna chiusura indicata.</Typography>
        )}
        {visibleClosures.map(({ closure, index }) => (
          <ClosureRow
            key={closure.key} id={`chiusura-${closure.key}`} closure={closure}
            errors={errorsFor(errors, "closure", index)}
            onChange={(next) => updateClosure(index, next)}
            onRemove={() => change({ ...draft, closures: removeItem(draft.closures, index) })}
          />
        ))}
      </Card>
      <Button
        variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addClosure}
        sx={{ borderStyle: "dashed", borderWidth: 1.5, "&:hover": { borderStyle: "dashed", borderWidth: 1.5 } }}
      >
        Aggiungi una chiusura
      </Button>

      {/* Barra di salvataggio sempre in vista */}
      <Box
        sx={{
          position: "sticky", bottom: 0, zIndex: 5, mt: 3, mx: { xs: -1.5, md: -3 }, px: { xs: 1.5, md: 3 }, py: 1.5,
          backgroundColor: "rgba(255,255,255,0.96)", borderTop: "1px solid #E8E8E4", backdropFilter: "blur(6px)",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap">
          <Box sx={{ minWidth: 0, flex: "1 1 120px" }}>
            {message ? (
              <Alert severity={message.severity} sx={{ py: 0 }}>{message.text}</Alert>
            ) : (
              <Typography fontSize={13} color={dirty ? "warning.dark" : "text.secondary"} fontWeight={dirty ? 700 : 400}>
                {dirty ? "Hai modifiche non salvate." : "Tutto salvato."}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained" size="large" onClick={save} disabled={saving || !dirty}
            sx={{ px: 4, borderRadius: "10px", minWidth: 140 }}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : "Salva"}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
